import type { AnswerSpec } from "./generated/AnswerSpec";

// Shared by the learner actor (authoritative grading) and the learner app (instant
// local check against an `AnswerCheck` digest). Both sides must normalise the same way,
// so this is the only implementation.

/**
 * Canonical form of an answer, or `null` when it cannot be an answer of that kind.
 *
 * - choice: the index as a decimal integer (`"02"` becomes `"2"`);
 * - number: a reduced fraction `n/d`, or `n` when whole. Accepts `3/4`, `0.75`, `0,75`,
 *   `-1.5`, spaces as digit groups; so `0.75`, `3/4` and `6/8` are the same answer;
 * - text: lower case, accents kept, inner whitespace collapsed.
 */
export function normalizeAnswer(kind: AnswerSpec["kind"], raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  switch (kind) {
    case "choice":
      return /^\d{1,2}$/.test(trimmed) ? String(Number(trimmed)) : null;
    case "number":
      return normalizeNumber(trimmed);
    case "text":
      return trimmed.toLocaleLowerCase().replace(/\s+/g, " ");
  }
}

function normalizeNumber(value: string): string | null {
  const compact = value.replace(/[\s\u00a0\u202f]/g, "").replace(",", ".");
  const fraction = /^(-?\d{1,12})\/(\d{1,12})$/.exec(compact);
  if (fraction !== null) {
    const [, n, d] = fraction;
    if (n === undefined || d === undefined) return null;
    return reduce(BigInt(n), BigInt(d));
  }
  const decimal = /^(-?)(\d{1,12})(?:\.(\d{1,9}))?$/.exec(compact);
  if (decimal === null) return null;
  const [, sign = "", whole = "0", frac = ""] = decimal;
  const scale = 10n ** BigInt(frac.length);
  const magnitude = BigInt(whole) * scale + BigInt(frac === "" ? "0" : frac);
  return reduce(sign === "-" ? -magnitude : magnitude, scale);
}

function reduce(n: bigint, d: bigint): string | null {
  if (d === 0n) return null;
  const g = gcd(n < 0n ? -n : n, d);
  const [rn, rd] = [n / g, d / g];
  // "-0" and "0" are the same answer.
  if (rn === 0n) return "0";
  return rd === 1n ? rn.toString() : `${rn.toString()}/${rd.toString()}`;
}

function gcd(a: bigint, b: bigint): bigint {
  let [x, y] = [a, b];
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

/** Lowercase hex SHA-256 of `salt:normalized`, as carried by `AnswerCheck.digest`. */
export async function answerDigest(salt: string, normalized: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${salt}:${normalized}`);
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(hash, (b) => b.toString(16).padStart(2, "0")).join("");
}
